import React, { useState } from 'react';
import type { PersonRef } from '../domain/people';
import { blockConsequences, type BlockPreview } from '../domain/safety';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { conversationTitle } from '../utils/conversations';
import { fullName } from '../utils/people';
import { ActionError, ErrorState } from './PageStates';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Blocking a person (017 FR-001, FR-002, FR-016): what it does, the group
 * chats we share — each unchecked, to leave in the same step — and Block.
 * The server confirms before anything changes; a failure keeps it open.
 */
export default function BlockDialog({
  person,
  open,
  onOpenChange,
  onDone,
}: {
  person: PersonRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-none bg-background">
        {open && (
          <BlockBody
            person={person}
            onCancel={() => onOpenChange(false)}
            onDone={() => {
              onOpenChange(false);
              onDone();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function BlockBody({
  person,
  onCancel,
  onDone,
}: {
  person: PersonRef;
  onCancel: () => void;
  onDone: () => void;
}) {
  const { safety } = useRepositories();
  const preview = useLoad<BlockPreview>(() => safety.blockPreview(person.id), person.id);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const toggle = (id: string, on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await safety.block(person.id, [...checked]);
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl">Block {fullName(person)}?</DialogTitle>
        <DialogDescription asChild>
          <ul className="list-disc space-y-1 pl-5 text-left">
            {blockConsequences(person.firstName).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </DialogDescription>
      </DialogHeader>

      {preview.status === 'loading' ? (
        <div role="status" aria-label="Loading group chats" className="space-y-2">
          <Skeleton className="h-12 rounded-2xl" />
        </div>
      ) : preview.status === 'error' ? (
        <ErrorState message="Couldn't load your group chats." onRetry={preview.reload} />
      ) : preview.data.sharedGroupConversations.length === 0 ? (
        <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
          You're not in any group chats together.
        </p>
      ) : (
        <fieldset>
          <legend className="mb-3 text-sm font-semibold text-foreground">
            Also leave group chats you're both in:
          </legend>
          <ul className="space-y-2">
            {preview.data.sharedGroupConversations.map((c) => {
              const id = `leave-${c.id}`;
              return (
                <li key={c.id} className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3">
                  <Checkbox
                    id={id}
                    checked={checked.has(c.id)}
                    onCheckedChange={(on) => toggle(c.id, on === true)}
                  />
                  <Label htmlFor={id} className="flex-1 cursor-pointer font-medium">
                    {conversationTitle(c)}
                  </Label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}

      {error !== null && <ActionError error={error} />}

      <DialogFooter className="gap-2">
        <Button variant="ghost" className="rounded-full" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          className="rounded-full"
          disabled={busy || preview.status !== 'ready'}
          onClick={confirm}
        >
          Block
        </Button>
      </DialogFooter>
    </>
  );
}
